import type {
  Actor,
  FeedbackCategory,
  FeedbackPageKey,
  IntegrationScope,
  Operation,
  BoardChanges,
  BoardPage,
  BoardPageRequest,
  BoardSnapshot,
  Card,
  MindmapLinks,
  MindmapNode,
  MindmapSave,
  MindmapView,
  OperationBatch,
  ShareRole,
  Tenant,
  User,
  UserRole,
  WorkflowLinks,
  WorkflowSave,
  WorkflowView,
  ProjectLabels,
  ProjectMember,
  ProjectSummary,
} from '@blueprint/domain'

export type DataMode = 'memory' | 'api'

export interface MutationAck {
  mutationId: string
  revision: number
  changes: BoardChanges
}

export type RepositoryErrorKind =
  | 'not_found'
  | 'conflict'
  | 'validation'
  // No usable session. The app shows the sign-in screen rather than an error.
  | 'unauthorized'
  | 'forbidden'
  | 'network'
  | 'server'

/** Public configuration, read before anyone has signed in. */
export interface AuthConfig {
  /** Present only while Teamcentric Accounts is on: it creates and manages tenants. */
  accounts?: boolean
  accountsUrl?: string
}

/** The teamcentric app switcher's entries, from Teamcentric Accounts. */
export interface AccountsApps {
  apps: { productId: string; name: string; company: string; url: string }[]
  accountUrl: string
  /** The account entry's label; "Your account" when absent (platform staff: "teamcentric Accounts"). */
  accountLabel?: string
  /** Only for an account owner. */
  billingUrl: string | null
}

/** One feedback submission, sent on to Teamcentric Accounts. */
export interface FeedbackInput {
  pageKey: FeedbackPageKey
  category?: FeedbackCategory
  message: string
  /** One screenshot, as a data URL. */
  screenshot?: string
}

/** Who is signed in, and whether their tenant is on a Teamcentric Accounts trial (no exports). */
export interface SignedIn {
  user: Actor
  trial: boolean
  /** Set while an Accounts super admin is switched into this demo user (Switch as): where Back to Accounts goes. */
  backToAccountsUrl: string | null
}

/** A colleague a board is shared with, as the share list shows them. */
export interface BoardShareEntry {
  userId: string
  email: string
  displayName: string
  role: ShareRole
}

/** Public token details; the secret is returned only by creation. */
export interface IntegrationToken {
  id: string
  name: string
  prefix: string
  scopes: IntegrationScope[]
  permissions: IntegrationPermissions | null
  createdAt: string
  lastUsedAt: string | null
  expiresAt: string | null
  revokedAt: string | null
}

export interface IntegrationPermissions {
  dashboard: { list: boolean; create: boolean }
  boardAccess: 'all' | 'selected'
  boardIds: string[]
  operations: Operation['type'][]
}

export interface BoardVersion {
  revision: number
  source: 'before_ai' | 'ai'
  integrationName: string | null
  createdAt: string
}

/** One saved version of a card's content. */
export interface CardVersion {
  version: number
  createdAt: string
}

export interface NewUserInput {
  email: string
  displayName: string
  role: UserRole
}

export interface UserChangesInput {
  email?: string
  displayName?: string
  role?: UserRole
  disabled?: boolean
}

export class RepositoryError extends Error {
  readonly kind: RepositoryErrorKind
  /** Present on a conflict so the UI can offer reload against the server's revision. */
  readonly currentRevision: number | undefined
  readonly details: Record<string, string> | undefined
  readonly requestId: string | undefined

  constructor(
    kind: RepositoryErrorKind,
    message: string,
    options: { currentRevision?: number; details?: Record<string, string>; requestId?: string } = {},
  ) {
    super(message)
    this.name = 'RepositoryError'
    this.kind = kind
    this.currentRevision = options.currentRevision
    this.details = options.details
    this.requestId = options.requestId
  }

  /** Network and server faults are worth retrying with the identical payload; the rest are not. */
  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'server'
  }
}

/**
 * The single storage seam. Stage 1 ships the in-browser memory adapter; stage 2 adds an
 * HTTP adapter behind this same interface. No React module may know which one is in use.
 */
export interface BoardRepository {
  readonly mode: DataMode
  /** Human-readable durability note shown next to the save status. */
  readonly durability: string
  // --- identity ---------------------------------------------------------------
  /** The signed-in user, or null when there is no session. Never throws for a 401. */
  currentUser(): Promise<SignedIn | null>
  /** What the web app needs before anyone is signed in. */
  authConfig(): Promise<AuthConfig>
  /** With Teamcentric Accounts on, the address that signs the person out of teamcentric too; otherwise null. */
  logOut(): Promise<string | null>
  /** The app switcher's entries; fails while Teamcentric Accounts is off. */
  accountsApps(): Promise<AccountsApps>
  updateProfile(displayName: string): Promise<Actor>
  listIntegrationTokens(): Promise<IntegrationToken[]>
  createIntegrationToken(input: { name: string; scopes: IntegrationScope[]; permissions?: IntegrationPermissions; expiresInDays?: number }): Promise<{ token: IntegrationToken; secret: string }>
  revokeIntegrationToken(id: string): Promise<void>

  // --- users (inside one tenant) ------------------------------------------------
  listUsers(): Promise<User[]>
  /** `invitationSent`: whether Teamcentric Accounts was asked to send the invitation. */
  createUser(input: NewUserInput): Promise<User & { invitationSent?: boolean }>
  updateUser(id: string, changes: UserChangesInput): Promise<User>
  /** Sends a Teamcentric Accounts invitation not accepted yet again; whether it was sent. */
  reinviteUser(id: string): Promise<boolean>
  /** The signed-in tenant admin's own tenant. */
  getTenant(): Promise<Tenant>
  /** Turns the MCP endpoint on or off for the signed-in admin's tenant. */
  setTenantMcpEnabled(enabled: boolean): Promise<Tenant>
  /** Whether the signed-in tenant admin has Billing at Teamcentric Accounts. */
  billingAvailable(): Promise<boolean>
  /** The one-time address at Teamcentric Accounts that opens Billing. */
  billingHandoff(): Promise<string>

  // --- feedback (tenant users, Teamcentric Accounts) ---------------------------
  /** The question Teamcentric Accounts has for the page, or the generic one. */
  feedbackQuestion(pageKey: FeedbackPageKey): Promise<string>
  sendFeedback(input: FeedbackInput): Promise<void>

  // --- sharing ------------------------------------------------------------------
  listShares(boardId: string): Promise<BoardShareEntry[]>
  shareBoard(boardId: string, userId: string, role: ShareRole): Promise<BoardShareEntry[]>
  unshareBoard(boardId: string, userId: string): Promise<BoardShareEntry[]>
  reassignOwner(boardId: string, newOwnerId: string): Promise<void>

  /** One page of boards. The window is the server's to honour and to clamp. */
  listBoards(page?: BoardPageRequest): Promise<BoardPage>
  /** `id` is client-generated so a lost response can be retried without a duplicate board. */
  createBoard(input: { id: string; name: string; projectId?: string }): Promise<BoardSnapshot>
  getBoard(id: string): Promise<BoardSnapshot>
  getBoardRevision?(id: string): Promise<number>
  listBoardVersions?(id: string): Promise<BoardVersion[]>
  getBoardVersion?(id: string, revision: number): Promise<BoardSnapshot>
  /** Newest first. */
  listCardVersions?(boardId: string, cardId: string): Promise<CardVersion[]>
  getCardVersion?(boardId: string, cardId: string, version: number): Promise<Card>
  deleteBoard(id: string, expectedRevision: number): Promise<void>
  /** Into `projectId`, or the server's choice of project when left out. */
  importBoard(snapshot: BoardSnapshot, projectId?: string): Promise<BoardSnapshot>
  applyOperations(boardId: string, batch: OperationBatch): Promise<MutationAck>

  // --- mindmaps (board rows of kind 'mindmap'; list, share and delete as boards) --
  /** `id` is client-generated, as for boards. `root` is given by an import. */
  createMindmap(input: { id: string; name: string; root?: MindmapNode; projectId?: string }): Promise<MindmapView>
  getMindmap(id: string): Promise<MindmapView>
  saveMindmap(id: string, save: MindmapSave): Promise<MindmapView>
  /** Story maps generated from the mindmap, and which of their cards came from which node. */
  listMindmapLinks(id: string): Promise<MindmapLinks>

  // --- workflows (board rows of kind 'workflow'; list, share and delete as boards) --
  /** `id` is client-generated, as for boards. */
  createWorkflow(input: { id: string; name: string; projectId?: string }): Promise<WorkflowView>
  getWorkflow(id: string): Promise<WorkflowView>
  saveWorkflow(id: string, save: WorkflowSave): Promise<WorkflowView>
  /** Story maps generated from this workflow, for updating one in place. */
  listWorkflowLinks(id: string): Promise<WorkflowLinks>

  // --- projects (every board row of any kind is in exactly one) --------------------
  listProjects(): Promise<ProjectSummary[]>
  /** `id` is client-generated, as for boards. */
  createProject(input: { id: string; name: string }): Promise<ProjectSummary>
  renameProject(id: string, name: string): Promise<ProjectSummary>
  /** Deletes the project and everything in it. */
  deleteProject(id: string): Promise<void>
  listProjectMembers(id: string): Promise<ProjectMember[]>
  setProjectMember(id: string, userId: string, role: ShareRole): Promise<ProjectMember[]>
  removeProjectMember(id: string, userId: string): Promise<ProjectMember[]>
  moveBoard(boardId: string, projectId: string): Promise<void>
  /** Tags and structure titles used in the board's project, for pick-or-type fields. */
  projectLabels(boardId: string): Promise<ProjectLabels>
  /** The project an item of any kind is in. */
  boardProject(boardId: string): Promise<string>
}
